import React, { useState, useEffect } from "react";
import { apiService } from "../../utils/api";
import { FaCalendarAlt, FaClock, FaCheckCircle, FaTimesCircle, FaHourglassHalf, FaSearch, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import "./AttendanceHistory.css";

function AttendanceHistory() {
  const [attendances, setAttendances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [monthView, setMonthView] = useState(false);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchAttendanceHistory = async () => {
      try {
        setLoading(true);
        const response = await apiService.getAttendanceHistory();
        if (response.data.success) {
          setAttendances(response.data.attendances);
        }
      } catch (err) {
        console.error("Error fetching attendance history:", err);
        setError(err.response?.data?.message || err.message || "An error occurred while fetching attendance history");
      } finally {
        setLoading(false);
      }
    };

    fetchAttendanceHistory();
  }, []);

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const formatTime = (timeString) => {
    return timeString ? new Date(`2000-01-01T${timeString}`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "N/A";
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "approved": return "status-approved";
      case "rejected": return "status-rejected";
      default: return "status-pending";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved": return <FaCheckCircle className="status-icon" />;
      case "rejected": return <FaTimesCircle className="status-icon" />;
      default: return <FaHourglassHalf className="status-icon" />;
    }
  };

  const filteredAttendances = attendances
    .filter(attendance => {
      if (filter === "all") return true;
      return attendance.status === filter;
    })
    .filter(attendance => {
      if (!searchTerm) return true;
      const date = formatDate(attendance.date).toLowerCase();
      const notes = (attendance.notes || "").toLowerCase();
      const status = attendance.status.toLowerCase();
      return date.includes(searchTerm.toLowerCase()) || 
             notes.includes(searchTerm.toLowerCase()) ||
             status.includes(searchTerm.toLowerCase());
    });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredAttendances.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredAttendances.length / itemsPerPage);

  // Stats calculation
  const totalAttendances = attendances.length;
  const approvedCount = attendances.filter(a => a.status === "approved").length;
  const rejectedCount = attendances.filter(a => a.status === "rejected").length;
  const pendingCount = attendances.filter(a => a.status === "pending").length;
  const approvedPercentage = totalAttendances ? Math.round((approvedCount / totalAttendances) * 100) : 0;

  if (loading) return (
    <div className="attendance-loading">
      <div className="loading-spinner"></div>
      <p>Loading your attendance records...</p>
    </div>
  );
  
  if (error) return (
    <div className="attendance-error">
      <div className="error-icon">!</div>
      <h3>Something went wrong</h3>
      <p>{error}</p>
      <button onClick={() => window.location.reload()} className="retry-button">
        Try Again
      </button>
    </div>
  );

  return (
    <div className="attendance-history-container">
      <div className="attendance-header">
        <h1>Attendance History</h1>
        <div className="view-toggle">
          {/* <button 
            className={!monthView ? "active" : ""} 
            onClick={() => setMonthView(false)}
          >
            List View
          </button>
          <button 
            className={monthView ? "active" : ""} 
            onClick={() => setMonthView(true)}
          >
            Calendar View
          </button> */}
        </div>
      </div>
      
      <div className="attendance-stats">
        <div className="stat-card total">
          <div className="stat-value">{totalAttendances}</div>
          <div className="stat-label">Total Records</div>
        </div>
        <div className="stat-card approved">
          <div className="stat-value">{approvedCount}</div>
          <div className="stat-label">Approved</div>
        </div>
        <div className="stat-card rejected">
          <div className="stat-value">{rejectedCount}</div>
          <div className="stat-label">Rejected</div>
        </div>
        <div className="stat-card pending">
          <div className="stat-value">{pendingCount}</div>
          <div className="stat-label">Pending</div>
        </div>
        {/* <div className="stat-card percentage">
          <div className="stat-value">{approvedPercentage}%</div>
          <div className="stat-label">Attendance Rate</div>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${approvedPercentage}%` }}
            ></div>
          </div>
        </div> */}
      </div>
      
      <div className="attendance-filters">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input 
            type="text" 
            placeholder="Search by date, status or notes..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filter-buttons">
          <button 
            className={filter === "all" ? "active" : ""} 
            onClick={() => setFilter("all")}
          >
            All
          </button>
          <button 
            className={filter === "approved" ? "active" : ""} 
            onClick={() => setFilter("approved")}
          >
            Approved
          </button>
          <button 
            className={filter === "rejected" ? "active" : ""} 
            onClick={() => setFilter("rejected")}
          >
            Rejected
          </button>
          <button 
            className={filter === "pending" ? "active" : ""} 
            onClick={() => setFilter("pending")}
          >
            Pending
          </button>
        </div>
      </div>
      
      {filteredAttendances.length === 0 ? (
        <div className="no-records">
          <div className="empty-illustration"></div>
          <h3>No attendance records found</h3>
          <p>There are no attendance records matching your current filters.</p>
        </div>
      ) : monthView ? (
        <div className="calendar-view">
          {/* Calendar view implementation would go here */}
          <div className="calendar-placeholder">
            <p>Calendar view is under development</p>
          </div>
        </div>
      ) : (
        <>
          <div className="attendance-cards">
            {currentItems.map((attendance) => (
              <div key={attendance.id} className={`attendance-card ${attendance.status}`}>
                <div className="card-header">
                  <div className="date-info">
                    <FaCalendarAlt className="date-icon" />
                    <span>{formatDate(attendance.date)}</span>
                  </div>
                  <span className={`status-badge ${getStatusClass(attendance.status)}`}>
                    {getStatusIcon(attendance.status)}
                    {attendance.status.charAt(0).toUpperCase() + attendance.status.slice(1)}
                  </span>
                </div>
                <div className="card-body">
                  <div className="time-info">
                    <FaClock className="time-icon" />
                    <span>Check-in: {formatTime(attendance.checkInTime)}</span>
                  </div>
                  <div className="notes-section">
                    <h4>Notes:</h4>
                    <p>{attendance.notes || "No notes provided"}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="pagination">
            <button 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="page-button"
            >
              <FaChevronLeft />
            </button>
            <span className="page-info">
              Page {currentPage} of {totalPages || 1}
            </span>
            <button 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || totalPages === 0}
              className="page-button"
            >
              <FaChevronRight />
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default AttendanceHistory;